// 🔐 accounts (09-30): the sign-in card (Nina's pick 09-30: option A, "Keep
// your seat", in every room). Used by the sheet (AccountLayer) and /account.
// Copy rules: short, no em dashes. The fine print must stay true to
// privacy.html: we keep the email from Google and nothing else.
//
// Two steps (R10, Nina 09-30 "Ask first"): Google, then, when this browser
// holds rooms or AIs the account doesn't have yet, "Found on this browser":
// all ticked, and the person keeps what is theirs (a shared laptop holds other
// people's seats; nothing is attached without this step).
import React, { useState } from 'react';
import GoogleButton from './GoogleButton';
import { signInWithGoogle, keepFound } from './accountState';

export function savedLine({ rooms = 0, agents = 0 } = {}) {
  const r = rooms === 1 ? '1 room' : `${rooms} rooms`;
  const a = agents === 1 ? '1 AI' : `${agents} AIs`;
  if (rooms && agents) return `Signed in. ${r} and ${a} are saved.`;
  if (rooms) return `Signed in. ${r} ${rooms === 1 ? 'is' : 'are'} saved.`;
  if (agents) return `Signed in. ${a} ${agents === 1 ? 'is' : 'are'} saved.`;
  return 'Signed in. Your seats will follow you.';
}

/** Step 2: what this browser holds, all ticked. Closing it keeps nothing. */
export function FoundStep({ found, onDone, closable = false, titleId = 'ms-signin-title' }) {
  const [rooms, setRooms] = useState(() => new Set(found.rooms.map((r) => r.room_id)));
  const [agents, setAgents] = useState(() => new Set(found.agents.map((a) => a.agent_id)));
  const [busy, setBusy] = useState(false);
  const flip = (setter, id) => setter((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const finish = async (keep) => {
    if (busy) return;
    setBusy(true);
    const r = await keepFound(keep
      ? { rooms: found.rooms.filter((x) => rooms.has(x.room_id)), agents: found.agents.filter((a) => agents.has(a.agent_id)) }
      : {});
    if (onDone) onDone(r);
  };
  const ticked = rooms.size + agents.size;
  return (
    <div className="ms-acct-card">
      {closable && <button type="button" className="ms-acct-x" onClick={() => finish(false)} aria-label="Close" disabled={busy}>✕</button>}
      <h2 id={titleId}>Found on this browser</h2>
      <p>Keep the ones that are yours.</p>
      <ul className="ms-found">
        {found.rooms.map((r) => (
          <li key={r.room_id}>
            <label>
              <input type="checkbox" checked={rooms.has(r.room_id)} onChange={() => flip(setRooms, r.room_id)} disabled={busy} />
              <span><b>{r.room_name || 'A room'}</b><small>You're {r.seat_name}</small></span>
            </label>
          </li>
        ))}
        {found.agents.map((a) => (
          <li key={a.agent_id}>
            <label>
              <input type="checkbox" checked={agents.has(a.agent_id)} onChange={() => flip(setAgents, a.agent_id)} disabled={busy} />
              <span><b>{a.name || 'An AI'}</b><small>Your AI</small></span>
            </label>
          </li>
        ))}
      </ul>
      {found.taken > 0 && (
        <p className="ms-acct-fine">{found.taken === 1 ? '1 more here is' : `${found.taken} more here are`} saved to another account.</p>
      )}
      <div className="ms-found-actions">
        <button type="button" className="ms-acct-btn" disabled={busy || ticked === 0} onClick={() => finish(true)}>{busy ? 'Saving…' : 'Keep these'}</button>
        <button type="button" className="ms-acct-link" disabled={busy} onClick={() => finish(false)}>None are mine</button>
      </div>
    </div>
  );
}

export default function SignInCard({ clientId, dark = true, onDone, onClose, titleId = 'ms-signin-title' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [found, setFound] = useState(null);

  const onCredential = async (credential) => {
    if (!credential || busy) return;
    setBusy(true);
    setError(null);
    const r = await signInWithGoogle(credential);
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    if (r.found) { setFound(r.found); return; }
    if (onDone) onDone(r);
  };

  if (found) return <FoundStep found={found} titleId={titleId} closable={!!onClose} onDone={(r) => { if (onDone) onDone(r); }} />;

  return (
    <div className="ms-acct-card">
      {onClose && <button type="button" className="ms-acct-x" onClick={onClose} aria-label="Close">✕</button>}
      <h2 id={titleId}>Keep your seat on every device</h2>
      <p>Your rooms, your names and your AIs, on your phone and your laptop.</p>
      {clientId
        ? <GoogleButton clientId={clientId} dark={dark} onCredential={onCredential} />
        : <p className="ms-acct-busy">One moment…</p>}
      {busy && <p className="ms-acct-busy" role="status">Signing you in…</p>}
      {error && <p className="ms-acct-error" role="alert">{error}</p>}
      <span className="ms-acct-fine">
        Optional. From Google we keep your email, nothing else.{' '}
        <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms</a> · <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy</a>
      </span>
    </div>
  );
}
